import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Layers, Loader2, Sparkles, Play, Check, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);
const STYLES = [
  { id: "realistic", label: "Realistic", emoji: "📷" },
  { id: "cartoon", label: "Cartoon", emoji: "🎨" },
  { id: "pixel", label: "Pixel", emoji: "👾" },
  { id: "lowpoly", label: "Low Poly", emoji: "🔷" },
  { id: "fantasy", label: "Fantasy", emoji: "🧙" },
  { id: "voxel", label: "Voxel", emoji: "🧊" },
];

export default function MaterialStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [applied, setApplied] = useState(null);
  const [surfaces, setSurfaces] = useState([]);
  const [surface, setSurface] = useState("grass");
  const [style, setStyle] = useState("realistic");
  const [generating, setGenerating] = useState(false);
  const [applyingUrl, setApplyingUrl] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/materials/${gameId}`);
      setMaterials(data?.materials || []);
      setApplied(data?.applied || null);
      setSurfaces(data?.surfaces || []);
    } catch (e) { toast.error("Couldn't open Material Studio."); }
    finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/materials/${gameId}/generate`, { surface, style }, { timeout: 90000 });
      setMaterials((l) => [data, ...l]);
      toast.success(`🧱 New ${surface} texture ready — apply it to your ground!`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't generate that texture.");
    } finally { setGenerating(false); }
  };

  const applyMaterial = async (m) => {
    setApplyingUrl(m.url);
    try {
      await api.put(`/materials/${gameId}/apply`, { url: m.url });
      setApplied(m.url);
      toast.success("✅ Applied to your ground — hit Play to see it!");
    } catch (e) { toast.error("Couldn't apply that texture."); }
    finally { setApplyingUrl(null); }
  };

  const reset = async () => {
    setApplyingUrl("__reset__");
    try {
      await api.put(`/materials/${gameId}/apply`, { url: "" });
      setApplied(null);
      toast.success("Reverted to the default ground.");
    } catch (e) { toast.error("Couldn't reset."); }
    finally { setApplyingUrl(null); }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  const surfList = surfaces.length ? surfaces : ["grass", "stone", "metal", "sand", "wood", "snow"];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="material-studio">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="material-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 truncate"><Layers className="w-6 h-6 text-amber-600" /> Material Studio</h1>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">AI-reskin your world's ground into new PBR surfaces</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {applied && <Button variant="outline" size="sm" className="rounded-full" onClick={reset} disabled={applyingUrl === "__reset__"} data-testid="material-reset-btn">{applyingUrl === "__reset__" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Reset</Button>}
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate(`/app/arcade/${gameId}`)} data-testid="material-play-btn"><Play className="w-4 h-4" /> Play</Button>
        </div>
      </div>

      {/* Generator */}
      <div className="rounded-2xl border border-amber-600/40 bg-gradient-to-br from-amber-500/10 to-orange-500/5 p-4 mb-6">
        <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-2">Surface</p>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="material-surfaces">
          {surfList.map((s) => (
            <button key={s} onClick={() => setSurface(s)} data-testid={`material-surface-${s}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${surface === s ? "border-amber-600 bg-amber-500/10 text-amber-700 dark:text-amber-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>{s}</button>
          ))}
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-2">Style</p>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="material-styles">
          {STYLES.map((s) => (
            <button key={s.id} onClick={() => setStyle(s.id)} data-testid={`material-style-${s.id}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${style === s.id ? "border-amber-600 bg-amber-500/10 text-amber-700 dark:text-amber-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>{s.emoji} {s.label}</button>
          ))}
        </div>
        <Button className="rounded-full" onClick={generate} disabled={generating} data-testid="material-generate-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating ? "Weaving…" : `Generate ${surface} texture`}
        </Button>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Uses 5 AI credits · tileable PBR texture, applied to your ground in-game.</p>
      </div>

      {materials.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-10 text-center" data-testid="material-empty">
          <Layers className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))] mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No textures yet — pick a surface + style and generate your first ground material.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="material-grid">
          {materials.map((m) => {
            const isApplied = applied === m.url;
            return (
              <div key={m.id} className={`rounded-2xl border overflow-hidden ${isApplied ? "border-amber-600 ring-2 ring-amber-500/40" : "border-[hsl(var(--border))]"}`} data-testid={`material-item-${m.id}`}>
                <div className="aspect-square" style={{ backgroundImage: `url(${mediaUrl(m.url)})`, backgroundSize: "50% 50%", backgroundRepeat: "repeat" }} />
                <div className="p-3">
                  <p className="text-sm font-semibold capitalize truncate">{m.surface} · <span className="text-[hsl(var(--muted-foreground))]">{m.style}</span></p>
                  <Button size="sm" variant={isApplied ? "secondary" : "outline"} className="rounded-full w-full mt-2" onClick={() => applyMaterial(m)} disabled={applyingUrl === m.url || isApplied} data-testid={`material-apply-${m.id}`}>
                    {applyingUrl === m.url ? <Loader2 className="w-4 h-4 animate-spin" /> : isApplied ? <Check className="w-4 h-4" /> : null} {isApplied ? "Applied" : "Apply to ground"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
